import {defineStore} from 'pinia'
import JwtService from "@/core/services/JwtService";
import {User} from "@/store/modules/AuthModule";
import ApiService from "@/core/services/ApiService";
import {clearImpersonationKeys} from "@/composables/useImpersonation";
import {clearCachedUnreadCount} from '@/services/notificationUnread.service';
import {clearPendingEmailReview} from '@/services/emailReviewNavigation.service';
import {webPushExplicitLogout, webPushIdentityConfirmed} from '@/services/webPush.service';

export const useUserStore = defineStore('userStore', {
  state: () => {
    return {
      user: {} as User,
      isAuthenticated: !!JwtService.getToken(),
      errors: {},
      _userResolved: false,
      _verifyPromise: null as Promise<void> | null,
      _verifyVersion: 0,
    }
  },
  actions: {
    isUserAuthenticated() {
      return !!JwtService.getToken();
    },
    logout(clearImpersonation = true) {
      this._verifyVersion++;
      if (clearImpersonation) webPushExplicitLogout();
      clearCachedUnreadCount(this.user?.id);
      clearPendingEmailReview(this.user?.id);
      this.isAuthenticated = false;
      this.user = {} as User;
      this.errors = [];
      // Only wipe the super-admin's backed-up token on an explicit, user-initiated
      // sign-out. On the verifyToken failure path the impersonated partner token may
      // have merely expired/been rejected while the impersonator's own token is still
      // valid — discarding it there would silently break "Back to my account".
      if (clearImpersonation) {
        clearImpersonationKeys();
      }
      JwtService.destroyToken();
    },
    login(credentials) {
      const version = ++this._verifyVersion;
      return ApiService.post("login", credentials)
        .then(({data}) => {
          if (version !== this._verifyVersion) return;
          if (!data.error) {
            // A fresh explicit sign-in owns the session: drop any impersonation
            // bookkeeping left behind by an expired delegated token (logout(false)
            // keeps it on purpose), so "Ieșire" can never resurrect an old
            // super-admin token on a shared machine.
            clearImpersonationKeys();
            this.isAuthenticated = true;
            this.user = data.user;
            this.errors = {};
            JwtService.saveToken(data.user.access_token);
            webPushIdentityConfirmed(Number(data.user.id));
          } else {
            this.errors = {...data.message.errors};
          }
        })
        .catch(({response}) => {
          if (version !== this._verifyVersion) return;
          this.errors = {...response.data.errors};
        });
    },

    verifyToken(payload) {
      ApiService.setHeader();

      // Pre-request: mark unresolved, increment monotonic version
      this._userResolved = false;
      const version = ++this._verifyVersion;

      const promise = ApiService.post("verify_token", payload)
        .then(({data}) => {
          // Discard if a newer verify was issued while this one was in-flight
          if (version !== this._verifyVersion) return;
          this.isAuthenticated = true;
          this.user = data.user;
          this.errors = {};
          this._userResolved = true;
          JwtService.saveToken(data.user.access_token);
          webPushIdentityConfirmed(Number(data.user.id));
        })
        .catch((err) => {
          if (version !== this._verifyVersion) return;
          this.errors = err?.response?.data?.errors ? {...err.response.data.errors} : {};
          this._userResolved = true;
          if (err?.response?.status === 401) {
            // Preserve the impersonator's backup token when only the delegated
            // session expired. The route guard performs the single redirect.
            this.logout(false);
          }
        });
      this._verifyPromise = promise;
      return promise;
    },
    resetLoginErrors() {
      this.errors = {};
    }
  },
})
